import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { LoginFormSchema } from '../../schema';

import { Colors, Logo } from '@/assets';
import { Alert, Checkbox, InputText, Loading, PrimaryButton } from '@/components';
import { AuthActions, AuthThunks, LoginForm, LoginGoogleButton } from '@/features/auth';
import { classValidatorFormResolverFactory } from '@/helper';
import { useIsMountedRef, useMessageData } from '@/hooks';
import { Screens } from '@/routes';

const LoginFormSchemaValidatorResolver = classValidatorFormResolverFactory<LoginFormSchema>(LoginFormSchema);

export const Login = () => {
    const { isSuccess, message, setMessage, clearMessage } = useMessageData();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const isMountedRef = useIsMountedRef();

    const form = useForm<LoginFormSchema>({
        resolver: LoginFormSchemaValidatorResolver,
        defaultValues: {
            rememberMe: true
        }
    });

    const onSubmit = async (data: LoginForm) => {
        try {
            setLoading(true);
            await dispatch(AuthThunks.login(data));
        } catch (error: any) {
            setMessage({ message: error.message });
        } finally {
            if (!isMountedRef.current) return;
            setLoading(false);
        }
    };

    return (
        <FormProvider {...form}>
            <Loading loading={loading} />
            <Logo />
            <Typography variant="h1" sx={{ mt: 3, fontWeight: 600, color: Colors.tangaroaBlue }}>
                Log in to your account
            </Typography>
            <Typography variant="h6" sx={{ mt: 2, fontWeight: 'normal', color: Colors.lynchBlue }}>
                Welcome back! Please enter your details.
            </Typography>
            <form style={{ width: '100%' }} onSubmit={form.handleSubmit(onSubmit)}>
                <Box sx={{ mt: 3 }}>
                    <Alert isSuccess={isSuccess} message={message} clearMessage={clearMessage} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <InputText name="email" label="Email" placeholder="Enter your email" />
                        <InputText name="password" type="password" label="Password" placeholder="••••••••" />
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Checkbox name="rememberMe" checked={true} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                    Remember for 30 days
                                </Typography>
                            </Box>
                            <Link to={Screens.ForgotPassword}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 500, color: Colors.slateBlueViolet }}>
                                    Forgot password
                                </Typography>
                            </Link>
                        </Box>
                        <PrimaryButton title="Sign in" submit />
                        <LoginGoogleButton />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                            <Typography variant="subtitle1" sx={{ color: Colors.lynchBlue, fontWeight: 'normal' }}>
                                Don’t have an account?
                            </Typography>
                            <Link to={Screens.Signup}>
                                <Typography variant="subtitle1" sx={{ color: Colors.slateBlueViolet, fontWeight: 500 }}>
                                    Sign up
                                </Typography>
                            </Link>
                        </Box>
                    </Box>
                </Box>
            </form>
        </FormProvider>
    );
};
